import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { Input, LoaderCircle } from '@skytech/ui'
import { useDebounce } from '@skytech/utils'
import { getQueryFilter } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { NoResults } from '@/components/NoResults'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'
import { commonEcomPolicyResources } from '@/resources/ecom-policy'
import { commonQueries } from '@/resources/queries'
import type { Policy } from '@/types'

import PolicySubscriptionItem from './PolicySubscriptionItem'

import './index.scss'

const PolicySubscriptions: FC = () => {
  const translate = useTranslation()
  const params = useParams()

  const costObjectId = Number(params.costObjectId)

  if (isNaN(costObjectId)) throw Error(translate('Employee ID is not a number!'))

  const [querySearch, setSearchQuery] = useSearchParamsByKey('policy-sub-search')
  const [searchFilter, setSearchFilter] = useState(querySearch || '')
  const debouncedAssetSearchTerm = useDebounce(searchFilter, 500)

  const { data: policyList, isLoading: isPoliciesLoading } = useQuery(
    commonQueries.policyList(),
    commonEcomPolicyResources.getList
  )

  const { data: costObjectDetails } = useQuery(queries.adminCostObjectDetails(costObjectId), () =>
    adminResources.getCostObjectDetails(costObjectId)
  )

  const stringMatcherPolicySub = (item: Policy & { isLocked: boolean }) => [
    item.name,
    item.vendorName,
    item.coverAmount,
    item.orderInterval,
    item.description,
    item.isLocked ? translate('Unlock ordering') : '',
  ]

  const merged =
    policyList?.map(policy => {
      const costObjectPolicy = costObjectDetails?.ecomPolicySubscriptions?.find(
        costObjectPolicy => costObjectPolicy.policyId === policy.id
      )

      return {
        ...policy,
        isLocked: costObjectPolicy?.isLocked || 0,
        isSubscribed: Boolean(costObjectPolicy),
      }
    }) || []
  const filteredPolicyList = merged.filter(
    policy =>
      policy.isActive &&
      (policy.customerHeadName || policy.customerId === costObjectDetails?.customerId) &&
      getQueryFilter(
        searchFilter,
        stringMatcherPolicySub
      )({ ...policy, isLocked: Boolean(policy.isLocked) })
  )

  useEffect(() => {
    setSearchFilter(querySearch || '')
  }, [querySearch])

  useEffect(() => {
    setSearchQuery(debouncedAssetSearchTerm)
  }, [debouncedAssetSearchTerm, setSearchQuery])

  return (
    <div className="policy-subscriptions">
      <div className="subs-table-actions">
        <div className="search">
          <Input
            id="policy-sub-search"
            type="text"
            value={searchFilter}
            placeholder={translate('Search')}
            onChange={e => setSearchFilter(e.target.value)}
          />

          <Translation
            id="({amountOfRecords} records)"
            amountOfRecords={String(filteredPolicyList.length)}
          />
        </div>
      </div>

      {!isPoliciesLoading && filteredPolicyList.length === 0 ? (
        <NoResults />
      ) : (
        <table className="shop-order-table">
          <thead>
            <tr>
              <th />
              <th>
                <Translation id="Name" />
              </th>
              <th className="is-mobile-hidden">
                <Translation id="Description" />
              </th>
              <th className="is-mobile-hidden text-right">
                <Translation id="Cover amount" />
              </th>
              <th className="is-mobile-hidden text-right">
                <Translation id="Cover interval" />
              </th>
              <th colSpan={2} className="center" />
            </tr>
          </thead>
          <tbody>
            {isPoliciesLoading ? (
              <LoaderCircle />
            ) : (
              filteredPolicyList.map(policy => (
                <PolicySubscriptionItem
                  key={policy.id}
                  policyDescription={policy.description}
                  policyId={policy.id}
                  vendorName={policy.vendorName}
                  policyName={policy.name}
                  coverAmount={policy.coverAmount}
                  orderInterval={policy.orderInterval}
                  searchFilter={searchFilter}
                  // todo: parse the response and remove Boolean()
                  isLocked={Boolean(policy.isLocked)}
                  isSubscribed={policy.isSubscribed}
                />
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  )
}

export default PolicySubscriptions
